// How useVenues decides where each venue comes from. Kept free of Firebase so
// the decisions are unit-tested (venueFetch.test.ts); the hook injects the
// real reads.

export const VENUE_TTL_MS = 24 * 60 * 60 * 1000

/** venueId → epoch millis of its last network read. */
export type VenueFetchTimes = Record<string, number>

export interface VenueReads<V> {
  /** Persistent-cache read; free, may return fewer than asked. */
  readCache: (ids: string[]) => Promise<Map<string, V>>
  /** Network read, billed per doc returned (min 1 per query). */
  readNetwork: (ids: string[]) => Promise<Map<string, V>>
}

/**
 * Loads `ids`: from the cache when its copy was read from the network within
 * the TTL, from the network otherwise (never fetched, stale, or missing from
 * the cache). Returns what was found and the ids that went to the network, so
 * the caller can stamp their fetch time.
 */
export async function loadVenues<V>(
  ids: string[],
  fetchedAt: VenueFetchTimes,
  now: number,
  reads: VenueReads<V>,
): Promise<{ venues: Map<string, V>; fetchedFromNetwork: string[] }> {
  const fresh = ids.filter(id => now - (fetchedAt[id] ?? 0) < VENUE_TTL_MS)
  const venues = fresh.length > 0 ? await reads.readCache(fresh) : new Map<string, V>()

  const toNetwork = ids.filter(id => !venues.has(id))
  if (toNetwork.length > 0) {
    for (const [id, venue] of await reads.readNetwork(toNetwork)) venues.set(id, venue)
  }
  return { venues, fetchedFromNetwork: toNetwork }
}
